import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

const PAGINATION_MODE_PARAM = 'blog_window';
const ENHANCED_PAGINATION_MODE = 'shifted';
const NATIVE_PAGINATION_MODE = 'native';
const TRANSITION_PAGINATION_MODE = 'transition';
const PAGINATION_BOUNDARY_PARAM = 'blog_boundary';

/**
 * Keeps a fixed featured article outside Shopify's native blog pagination.
 *
 * Shopify can paginate `blog.articles`, but it can't paginate a filtered copy
 * of that array. When the featured article is the first item in the active
 * blog view, each grid page therefore borrows one card from the next native
 * page so no article is lost and every full page keeps its configured size.
 *
 * @typedef {object} BlogPagePaginationRefs
 * @property {HTMLElement} grid
 * @property {HTMLElement[]} [cards]
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} [pagination]
 * @property {HTMLElement} [paginationNext]
 * @property {HTMLElement[]} [paginationParts]
 *
 * @extends {Component<BlogPagePaginationRefs>}
 */
class BlogPagePagination extends Component {
  requiredRefs = ['grid'];

  #balanceGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('data-balanced')) return;

    this.#startBalancing();
  }

  updatedCallback() {
    super.updatedCallback();
    this.removeAttribute('data-balanced');
    this.#startBalancing();
  }

  disconnectedCallback() {
    this.#balanceGeneration += 1;
    super.disconnectedCallback();
  }

  #startBalancing() {
    const generation = ++this.#balanceGeneration;

    void this.#balanceFeaturedArticle(generation);
  }

  /**
   * @param {number} generation
   */
  async #balanceFeaturedArticle(generation) {
    const { grid } = this.refs;

    if (!this.hasAttribute('data-shift-featured')) {
      this.#finishNativePagination(generation);
      return;
    }

    const currentPage = Number(this.dataset.currentPage || 1);
    const pageSize = Number(this.dataset.pageSize || 0);
    const gridPages = Number(this.dataset.gridPages || 0);
    const currentUrl = new URL(window.location.href);
    const requestedMode = currentUrl.searchParams.get(PAGINATION_MODE_PARAM);
    const transitionBoundary = Number(currentUrl.searchParams.get(PAGINATION_BOUNDARY_PARAM) || 0);
    const hasValidTransition =
      requestedMode === TRANSITION_PAGINATION_MODE &&
      Number.isInteger(transitionBoundary) &&
      transitionBoundary >= 1 &&
      transitionBoundary <= gridPages;
    const currentCards = Array.isArray(this.refs.cards) ? this.refs.cards : [];

    if (requestedMode === NATIVE_PAGINATION_MODE) {
      this.#finishNativePagination(generation);
      return;
    }

    if (requestedMode === TRANSITION_PAGINATION_MODE && !hasValidTransition) {
      this.#finishNativePagination(generation);
      return;
    }

    if (hasValidTransition && currentPage >= transitionBoundary) {
      const boundaryCard = currentPage === transitionBoundary && currentPage > 1 ? currentCards[0] : undefined;

      this.#finishTransitionPagination(generation, transitionBoundary, boundaryCard);
      return;
    }

    const enhancedRequest =
      requestedMode === ENHANCED_PAGINATION_MODE ||
      (currentPage === 1 && !requestedMode) ||
      (hasValidTransition && currentPage < transitionBoundary);

    if (!enhancedRequest) {
      this.#finishNativePagination(generation);
      return;
    }

    if (gridPages === 0) {
      this.#finishEnhancedPagination(generation);
      return;
    }

    if (pageSize < 1) {
      this.#finishNativePagination(generation);
      return;
    }

    if (currentPage > gridPages) {
      this.#redirectToEnhancedPage(generation, gridPages);
      return;
    }

    const cardBorrowedByPreviousPage = currentPage > 1 ? currentCards[0] : undefined;
    const nextUrlValue = this.dataset.nextUrl;

    if (!nextUrlValue) {
      cardBorrowedByPreviousPage?.remove();
      this.#finishEnhancedPagination(generation);
      return;
    }

    grid.setAttribute('aria-busy', 'true');
    grid.setAttribute('inert', '');
    this.refs.pagination?.setAttribute('inert', '');

    try {
      const nextUrl = new URL(nextUrlValue, window.location.origin);
      const nextSectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, true, nextUrl);

      if (!this.#isCurrentGeneration(generation)) return;

      const nextSection = new DOMParser().parseFromString(nextSectionHTML, 'text/html');
      const nextCard = nextSection.querySelector('[ref="grid"] > [data-blog-card]');

      if (!(nextCard instanceof HTMLElement)) {
        this.#finishTransitionPagination(generation, currentPage, cardBorrowedByPreviousPage);
        return;
      }

      cardBorrowedByPreviousPage?.remove();

      if (grid.childElementCount < pageSize) {
        grid.append(nextCard);
      }

      this.#finishEnhancedPagination(generation);
    } catch (error) {
      if (!this.#isCurrentGeneration(generation)) return;

      console.error('Unable to balance blog pagination.', error);
      this.#finishTransitionPagination(generation, currentPage, cardBorrowedByPreviousPage);
    }
  }

  /**
   * @param {number} generation
   */
  #finishEnhancedPagination(generation) {
    if (!this.#isCurrentGeneration(generation)) return;

    this.#clearPendingState();
    this.removeAttribute('data-balance-failed');
    this.removeAttribute('data-native-pagination');

    this.#syncEmptyState();
    this.#adjustPagination();
    this.#setNavigationMode('enhanced');
    this.setAttribute('data-balanced', '');
  }

  /**
   * @param {number} generation
   */
  #finishNativePagination(generation) {
    if (!this.#isCurrentGeneration(generation)) return;

    this.#clearPendingState();
    this.removeAttribute('data-balance-failed');

    this.#syncEmptyState();
    this.#setNavigationMode('native');
    this.setAttribute('data-native-pagination', '');
    this.setAttribute('data-balanced', '');
  }

  /**
   * If an enhanced page can't borrow its final card, keep the boundary card
   * on the previous enhanced page, remove it from this page, and switch all
   * following links back to native pagination. This creates one shorter page
   * without losing or duplicating an article across the navigation path.
   *
   * @param {number} generation
   * @param {number} boundaryPage
   * @param {HTMLElement | undefined} borrowedCard
   */
  #finishTransitionPagination(generation, boundaryPage, borrowedCard) {
    if (!this.#isCurrentGeneration(generation)) return;

    borrowedCard?.remove();

    this.#clearPendingState();
    this.removeAttribute('data-native-pagination');

    this.#syncEmptyState();
    this.#setNavigationMode('transition', boundaryPage);
    this.setAttribute('data-balance-failed', '');
    this.setAttribute('data-balanced', '');
  }

  /**
   * A stale enhanced URL can point at Shopify's surplus native last page.
   * Replace it with the actual last grid page so a card already borrowed by
   * the previous page never appears again on a phantom page.
   *
   * @param {number} generation
   * @param {number} lastPage
   */
  #redirectToEnhancedPage(generation, lastPage) {
    if (!this.#isCurrentGeneration(generation) || lastPage < 1) return;

    const { grid, pagination } = this.refs;
    const url = new URL(window.location.href);

    if (lastPage === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(lastPage));
    }

    url.searchParams.set(PAGINATION_MODE_PARAM, ENHANCED_PAGINATION_MODE);
    url.searchParams.delete(PAGINATION_BOUNDARY_PARAM);

    grid.setAttribute('aria-busy', 'true');
    grid.setAttribute('inert', '');
    pagination?.setAttribute('inert', '');
    this.setAttribute('data-redirecting', '');
    window.location.replace(`${url.pathname}${url.search}${url.hash}`);
  }

  #clearPendingState() {
    const { grid, pagination } = this.refs;

    grid.removeAttribute('aria-busy');
    grid.removeAttribute('inert');
    pagination?.removeAttribute('inert');
  }

  #syncEmptyState() {
    const { grid, empty } = this.refs;

    if (empty) empty.hidden = grid.childElementCount > 0;
  }

  #adjustPagination() {
    const { pagination, paginationNext } = this.refs;

    if (!pagination) return;

    const currentPage = Number(this.dataset.currentPage || 1);
    const gridPages = Number(this.dataset.gridPages || 0);
    const nativePages = Number(this.dataset.nativePages || 0);

    if (gridPages <= 1) {
      pagination.hidden = true;
      return;
    }

    pagination.hidden = false;

    if (nativePages > gridPages) {
      const parts = Array.isArray(this.refs.paginationParts) ? this.refs.paginationParts : [];
      const nativeLastPart = parts.find((part) => Number(part.dataset.pageNumber) === nativePages);
      const adjustedLastPart = parts.find((part) => Number(part.dataset.pageNumber) === gridPages);

      if (nativeLastPart && nativeLastPart !== adjustedLastPart) {
        if (adjustedLastPart) {
          nativeLastPart.remove();
        } else {
          this.#retargetPagePart(nativeLastPart, gridPages);
        }
      }
    }

    if (paginationNext) paginationNext.hidden = currentPage >= gridPages;
  }

  /**
   * @param {HTMLElement} part
   * @param {number} page
   */
  #retargetPagePart(part, page) {
    const href = part.getAttribute('href');

    part.dataset.pageNumber = String(page);
    part.textContent = String(page);

    if (!href) return;

    const url = new URL(href, window.location.origin);
    url.searchParams.set('page', String(page));
    part.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
  }

  /**
   * Carry the enhanced/native decision through page links so adjacent pages
   * never make conflicting assumptions about their shared boundary card.
   *
   * @param {'enhanced' | 'native' | 'transition'} mode
   * @param {number} [boundaryPage]
   */
  #setNavigationMode(mode, boundaryPage = 1) {
    const { pagination } = this.refs;

    if (!pagination) return;

    for (const link of pagination.querySelectorAll('a[href]')) {
      const href = link.getAttribute('href');

      if (!href) continue;

      const url = new URL(href, window.location.origin);
      const targetPage = Number(url.searchParams.get('page') || 1);

      if (mode === 'enhanced' || (mode === 'transition' && targetPage < boundaryPage)) {
        url.searchParams.set(PAGINATION_MODE_PARAM, ENHANCED_PAGINATION_MODE);
        url.searchParams.delete(PAGINATION_BOUNDARY_PARAM);
      } else if (mode === 'transition') {
        url.searchParams.set(PAGINATION_MODE_PARAM, TRANSITION_PAGINATION_MODE);
        url.searchParams.set(PAGINATION_BOUNDARY_PARAM, String(boundaryPage));
      } else {
        url.searchParams.set(PAGINATION_MODE_PARAM, NATIVE_PAGINATION_MODE);
        url.searchParams.delete(PAGINATION_BOUNDARY_PARAM);
      }

      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    }
  }

  /**
   * @param {number} generation
   */
  #isCurrentGeneration(generation) {
    return this.isConnected && generation === this.#balanceGeneration;
  }

  get sectionId() {
    const sectionId = this.getAttribute('section-id');

    if (!sectionId) throw new Error('The section-id attribute is required');

    return sectionId;
  }
}

if (!customElements.get('blog-page-pagination')) {
  customElements.define('blog-page-pagination', BlogPagePagination);
}
